import React from 'react';
import { css } from '@emotion/react';
import { color } from '@/styles/color';
import diaperSvg from '@/components/svg/diaper.svg';
import toiletSvg from '@/components/svg/toilet.svg';
import i18n, { t } from 'i18next';

interface ExportTimeRangeDisplayProps {
    detectUrinationHeatmap: DetectUrinationHeatmapProps;
}

interface DetectUrinationHeatmapProps {
    data: Record<string, boolean[]> | null;
    xCategories: string[];
    yCategories: string[];
}

interface InternalTimeRangeDisplayProps {
    categories: string[];
    filterCondition: Record<string, boolean[]>;
}

interface TimeRangeDisplayProps {
    icon: typeof diaperSvg;
    highlightText: string;
    highlightColor: string;
    categories: string[];
    filterCondition: boolean[];
}

// TimeRangeDisplays
const flexColumnContainer = css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: 24px;
    padding-bottom: 24px;
`;

const CardContainer = css`
    display: flex;
    flex-direction: column;
    padding: 16px;
    background-color: ${color.light_gray};
    border-radius: 8px;
    width: fit-content;
    border: 1px solid ${color.border_gray};
  `;

// TimeRangeDisplay
const titleContainer = css`
    display: flex;
    align-items: center;
    font-weight: bold;
    font-size: 20px;
    &:not(:first-of-type) {
      margin-top: 8px;
    }
  `;

const titleText = css`
margin: 0;
padding: 0;
white-space: pre-wrap;
`;

const iconStyle = css`
  width: 20px;
  height: 20px;
  margin-right: 4px;
  vertical-align: middle;
`;


const highlightedText = (highlightColor: string) => css`
    color: ${highlightColor};
  `;

const TimeRangeDisplays = React.memo(({ detectUrinationHeatmap }: ExportTimeRangeDisplayProps) => {
    const categories = detectUrinationHeatmap.xCategories;
    const filterCondition = detectUrinationHeatmap.data;

    if (!categories || !filterCondition) {
        return null;
    }

    return (
        <div css={flexColumnContainer}>
            <div css={CardContainer}>
                <PoolUrineTimeRangeDisplay categories={categories} filterCondition={filterCondition} />
                <UrinationTimeRangeDisplay categories={categories} filterCondition={filterCondition} />
            </div>
        </div>
    );
});

export default TimeRangeDisplays;

function PoolUrineTimeRangeDisplay({ categories, filterCondition }: InternalTimeRangeDisplayProps) {
    return TimeRangeDisplay({
        icon: toiletSvg,
        highlightText: t("estimatedToiletGuidance", "トイレ誘導の目安"),
        highlightColor: "#ffc000",
        categories: categories,
        filterCondition: filterCondition.poolUrine
    });
}

function UrinationTimeRangeDisplay({ categories, filterCondition }: InternalTimeRangeDisplayProps) {
    return TimeRangeDisplay({
        icon: diaperSvg,
        highlightText: t("estimatedDiaperUse", "おむつ交換の目安"),
        highlightColor: "#fab1bfff",
        categories: categories,
        filterCondition: filterCondition.urination
    });
}

function TimeRangeDisplay({
                              icon,
                              highlightText,
                              highlightColor,
                              categories,
                              filterCondition
                          }: TimeRangeDisplayProps) {

    const getHighlightedCategories = (categories: string[], filterCondition: boolean[]) => {

        let startIndex: number | null = null;
        let filteredCategories: string[] = [];

        [...filterCondition, false].forEach((condition, i) => {
            if (condition) {
                if (startIndex === null) {
                    startIndex = i;
                }
            } else if (startIndex !== null) {
                const continueCount = i - startIndex;
                if (continueCount >= 2) {
                    filteredCategories.push(`${categories[startIndex]}-/${categories[startIndex + continueCount - 1]}`);
                } else {
                    filteredCategories.push(categories[startIndex]);
                }
                startIndex = null;
            }
        });

        return filteredCategories;
    }

    const renderCategories = (categories: string[]) => {
        return categories
            .map((category, index, array) => (
                <span key={index}>
                    {category}
                    {index < array.length - 1 ? '-,\u00A0\u00A0' : '-'}
                </span>
            ));
    };

    const highlightedCategories = getHighlightedCategories(categories, filterCondition);

    return (
        <div css={titleContainer}>
            <p css={titleText}>
                <img
                    src={icon}
                    alt={highlightText}
                    css={iconStyle}
                />
                <span css={highlightedText(highlightColor)}>
                    {highlightText}
                </span>
                {'： '}
            </p>
            {highlightedCategories.length > 0 ? (
                renderCategories(highlightedCategories)
            ) : (
                <span>-</span>
            )}
        </div>
    );
}
